import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { CaraLoomLogo } from './CaraLoomLogo';
import { NotificationBell } from './NotificationBell';
import { ModeSwitcher } from './ModeSwitcher';
import { SuiteNav } from './SuiteNav';
import { Moon, Sun, LogOut, ChevronLeft, ChevronRight, ChevronDown, Sparkles, HelpCircle, Search } from 'lucide-react';
import { TierBadge } from './TierComponents';
import { isFeatureLive } from '../../lib/caraloom_v2_ia';
import { StatusBanner } from './StatusBanner';
import MfaNudgeBanner from '../security/MfaNudgeBanner';

/**
 * CaraLoom V2 IA — sidebar feature gating.
 *
 * Several existing dashboards (ClinicianBanking, ClinicianPayouts, RcmDashboard,
 * etc.) pass a bespoke `navItems` array directly to DashboardShell — they
 * don't go through navItems.js, so they bypass the central feature filter.
 *
 * Rather than touch all 6+ pages (large blast radius), DashboardShell now
 * applies a fallback href → featureId lookup so an item linking to an
 * archived route is hidden automatically, even when the caller forgot to
 * tag it with a `featureId`. New archived routes only need to be added to
 * this map.
 */
const HREF_TO_FEATURE_ID = {
  '/clinician/coach':           'workforce.ai-practice-coach',
  '/clinician/care-requests':   'care.care-requests-lynk',
  '/patient/care-requests':     'care.care-requests-lynk',
  '/patient/saved-searches':    'care.saved-searches',
  '/patient/saved-clinicians':  'care.saved-clinicians',
  '/patient/navigator':         'care.ai-care-navigator',
  '/care-library':              'seo.care-library',
  '/resources':                 'seo.authority-articles',
  '/home-care':                 'seo.local-care-pages',
};

const isItemLive = (item) => {
  // 1. Explicit featureId on the item wins (from navItems.js / filterLiveNav)
  if (item.featureId) return isFeatureLive(item.featureId);
  // 2. Fallback: derive featureId from href via the lookup map above
  if (item.href) {
    const [base] = item.href.split('#');
    const fid = HREF_TO_FEATURE_ID[base];
    if (fid) return isFeatureLive(fid);
  }
  return true;
};

/**
 * Floating glass-pill sidebar + transparent header shell used by
 * the internal CaraLoom dashboards. Collapsible on small screens.
 */
export const DashboardShell = ({
  navItems = [],
  title,
  subtitle,
  rightSlot = null,
  children,
  testId = 'dashboard-shell',
}) => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);

  // V2 IA: hide nav items that point at archived features. Items can opt
  // in via an explicit `featureId` field, or be filtered automatically via
  // the HREF_TO_FEATURE_ID lookup map above (handles dashboards that pass
  // bespoke nav arrays without featureId tags).
  navItems = navItems.filter(isItemLive);

  // Grouped, progressively-disclosed navigation. Items opt in via an optional
  // `group` field; dashboards without groups render the original flat list.
  const hasGroups = navItems.some((it) => it.group);
  const navGroups = (() => {
    const order = [];
    const map = {};
    navItems.forEach((it) => {
      const g = it.group || 'More';
      if (!map[g]) { map[g] = { name: g, items: [] }; order.push(map[g]); }
      map[g].items.push(it);
    });
    return order;
  })();
  // 'More' is the only group we hide by default. 'System' contains
  // Privacy & Data — a high-value item that needs to stay discoverable
  // (audit Issue 16 follow-up — a hidden 'Privacy & Data' link reads as
  // a dead-link to a reviewer scanning the sidebar).
  const DEFAULT_CLOSED_GROUPS = ['More'];
  const navStorageKey = `cl_nav_open_${user?.role || 'x'}`;
  const [openGroups, setOpenGroups] = useState(() => {
    try { return JSON.parse(localStorage.getItem(navStorageKey) || '{}'); } catch (e) { return {}; }
  });
  const toggleGroup = (name) => {
    setOpenGroups((prev) => {
      const current = prev[name] ?? !DEFAULT_CLOSED_GROUPS.includes(name);
      const next = { ...prev, [name]: !current };
      try { localStorage.setItem(navStorageKey, JSON.stringify(next)); } catch (e) { /* ignore */ }
      return next;
    });
  };

  const renderNavItem = (item) => {
    const active = item.active ?? (item.href && location.pathname === item.href);
    const Icon = item.icon;
    const base = `group flex items-center gap-3 px-3 py-2.5 rounded-2xl text-sm font-semibold transition-all`;
    const visual = item.highlight
      ? 'bg-gradient-to-r from-cn-primary to-cn-secondary text-white shadow-lg shadow-cn-primary/20'
      : active
      ? 'bg-cn-primary/10 text-cn-primary dark:bg-cn-primary/25 dark:text-white'
      : 'text-cn-text-muted hover:bg-cn-border/40 hover:text-cn-text dark:hover:bg-white/5';
    const inner = (
      <>
        <Icon className="w-5 h-5 flex-shrink-0" />
        {!collapsed && <span className="truncate">{item.label}</span>}
        {!collapsed && item.highlight && <Sparkles className="w-3.5 h-3.5 ml-auto opacity-80" />}
        {!collapsed && item.badge && (
          <span className="ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-cn-accent text-white">
            {item.badge}
          </span>
        )}
      </>
    );
    const testId = `nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`;
    if (item.onClick) {
      return (
        <button
          key={item.label}
          type="button"
          onClick={item.onClick}
          className={`${base} ${visual} w-full text-left`}
          data-testid={testId}
        >{inner}</button>
      );
    }
    if (item.href && item.href.startsWith('/')) {
      return <Link key={item.label} to={item.href} className={`${base} ${visual}`} data-testid={testId}>{inner}</Link>;
    }
    return <a key={item.label} href={item.href || '#'} className={`${base} ${visual}`} data-testid={testId}>{inner}</a>;
  };

  const handleLogout = async () => {
    // Iter 164 · Await logout so the refresh cookie is revoked
    // server-side BEFORE we navigate. Send the user to /auth (not /)
    // so they can sign back in without an extra click.
    await logout();
    navigate('/auth', { replace: true });
  };

  const hour = new Date().getHours();
  const greeting =
    hour < 5 ? 'Resting hours' :
    hour < 12 ? 'Good morning' :
    hour < 17 ? 'Good afternoon' :
    hour < 21 ? 'Good evening' :
    'Quiet night';

  return (
    <div className="min-h-screen bg-cn-bg dark:bg-[#020617] relative overflow-hidden" data-testid={testId}>
      {/* Ambient organic blobs */}
      <div className="pointer-events-none fixed -top-32 -left-32 w-[28rem] h-[28rem] rounded-full bg-cn-secondary/15 blur-[120px] animate-cn-aurora" />
      <div className="pointer-events-none fixed -bottom-40 -right-32 w-[32rem] h-[32rem] rounded-full bg-cn-accent/10 blur-[140px] animate-cn-aurora" style={{ animationDelay: '4s' }} />

      {/* Sidebar */}
      <aside
        className={`fixed left-4 top-4 bottom-4 z-50 hidden lg:flex flex-col cn-glass rounded-3xl border border-cn-border/60 p-3 transition-[width] duration-300 ${
          collapsed ? 'w-[72px]' : 'w-64'
        }`}
        data-testid="dashboard-sidebar"
      >
        {/* Brand + collapse */}
        <div className="flex items-center justify-between px-2 py-2 mb-4">
          {!collapsed ? (
            <Link to="/" className="flex items-center"><CaraLoomLogo /></Link>
          ) : (
            <Link to="/" className="mx-auto"><CaraLoomLogo showWordmark={false} /></Link>
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            data-testid="sidebar-collapse-btn"
            className={`p-1.5 rounded-full text-cn-text-muted hover:bg-cn-border/40 transition-colors ${collapsed ? 'hidden' : 'flex'}`}
            aria-label="Collapse sidebar"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
        </div>
        {collapsed && (
          <button
            onClick={() => setCollapsed(false)}
            data-testid="sidebar-expand-btn"
            className="self-center mb-3 p-1.5 rounded-full text-cn-text-muted hover:bg-cn-border/40"
            aria-label="Expand sidebar"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        )}

        {/* Quick search / command palette trigger */}
        <button
          type="button"
          onClick={() => window.dispatchEvent(new Event('open-command-palette'))}
          data-testid="command-palette-trigger"
          aria-label="Open quick search"
          title="Quick search (⌘K)"
          className={`mb-3 flex items-center gap-2 rounded-2xl border border-cn-border/70 bg-cn-bg/50 text-cn-text-muted hover:text-cn-text hover:border-cn-primary/40 transition-colors ${collapsed ? 'mx-auto p-2' : 'px-3 py-2'}`}
        >
          <Search className="w-4 h-4 flex-shrink-0" />
          {!collapsed && (
            <>
              <span className="text-sm flex-1 text-left">Quick search…</span>
              <kbd className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-cn-border/50 text-cn-text-muted">⌘K</kbd>
            </>
          )}
        </button>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto pr-1 space-y-1" data-testid="sidebar-nav">
          {!hasGroups && navItems.map((item) => renderNavItem(item))}
          {hasGroups && navGroups.map((grp) => {
            const showHeader = !collapsed && grp.name && grp.name !== 'Overview' && grp.name !== 'More';
            const isOpen = collapsed ? true : (openGroups[grp.name] ?? !DEFAULT_CLOSED_GROUPS.includes(grp.name));
            const slug = grp.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
            return (
              <div key={grp.name} className="space-y-1">
                {showHeader && (
                  <button
                    type="button"
                    onClick={() => toggleGroup(grp.name)}
                    data-testid={`nav-group-${slug}`}
                    className="w-full flex items-center justify-between px-3 pt-3 pb-1 text-[10px] font-bold uppercase tracking-[0.16em] text-cn-text-muted/80 hover:text-cn-text transition-colors"
                  >
                    <span>{grp.name}</span>
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${isOpen ? '' : '-rotate-90'}`} />
                  </button>
                )}
                {isOpen && grp.items.map((item) => renderNavItem(item))}
                {!collapsed && showHeader && <div className="h-px bg-cn-border/40 mx-3 mt-1" />}
              </div>
            );
          })}
        </nav>

        {/* User card */}
        <div className="mt-3 pt-3 border-t border-cn-border/60">
          {!collapsed ? (
            <div className="flex items-center gap-2 px-2 py-2 rounded-2xl bg-white/40 dark:bg-white/5">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-cn-primary to-cn-secondary text-white flex items-center justify-center text-sm font-bold overflow-hidden" data-testid="shell-user-avatar">
                {user?.photo_url ? (
                  <img src={user.photo_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <>{user?.first_name?.[0]}{user?.last_name?.[0]}</>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-cn-text truncate">{user?.first_name} {user?.last_name}</p>
                <p className="text-[11px] text-cn-text-muted capitalize">{user?.role}</p>
              </div>
              <button
                onClick={handleLogout}
                data-testid="logout-btn"
                className="p-1.5 rounded-full text-cn-text-muted hover:bg-cn-error/10 hover:text-cn-error transition-colors"
                title="Sign out"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="mx-auto flex w-10 h-10 items-center justify-center rounded-full text-cn-text-muted hover:bg-cn-error/10 hover:text-cn-error"
              title="Sign out"
            >
              <LogOut className="w-4 h-4" />
            </button>
          )}
        </div>
      </aside>

      {/* Mobile top brand */}
      <div className="lg:hidden sticky top-0 z-40 cn-glass border-b border-cn-border/50">
        <div className="flex items-center justify-between px-4 h-14">
          <Link to="/"><CaraLoomLogo /></Link>
          <div className="flex items-center gap-2">
            <NotificationBell />
            <button
              onClick={toggleTheme}
              className="p-2 rounded-full hover:bg-cn-border/40"
              aria-label="Toggle theme"
            >
              {theme === 'light' ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4 text-yellow-400" />}
            </button>
          </div>
        </div>
      </div>

      {/* Main */}
      <main className={`relative z-10 transition-[padding] duration-300 ${collapsed ? 'lg:pl-[96px]' : 'lg:pl-[288px]'} p-4 sm:p-6 lg:p-10`}>
        {/* HIPAA Readiness Iter 139: nudge clinicians + admins to enable
            two-factor auth. Backend gates via /api/auth/mfa-nudge-status. */}
        <MfaNudgeBanner />
        {/* In-app status banner — Blueprint #45. Auto-surfaces incidents
            from /api/status/active-incidents; dismissible per session. */}
        <StatusBanner />
        {/* CaraLoom v2 suite nav — shows which product suites are available
            to the current role. Source of truth: caraloom_v2_ia.js. */}
        <SuiteNav />
        {/* Header */}
        <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8 mt-4">
          <div>
            <p className="cn-eyebrow">{greeting}</p>
            <div className="flex items-center gap-3 flex-wrap mt-1">
              <h1 className="cn-display text-3xl sm:text-4xl text-cn-text dark:text-white">
                {title}
              </h1>
              <TierBadge className="self-center" />
            </div>
            {subtitle && (
              <p className="text-cn-text-muted dark:text-gray-400 mt-1 text-sm sm:text-base max-w-xl">{subtitle}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {rightSlot}
            <div className="hidden lg:flex items-center gap-2">
              <Link to="/help" aria-label="Help center" data-testid="header-help-link" className="p-2.5 rounded-full bg-white/60 dark:bg-white/5 border border-cn-border/60 hover:bg-white text-cn-text-muted hover:text-cn-primary transition-colors">
                <HelpCircle className="w-4 h-4" />
              </Link>
              <ModeSwitcher compact />
              <NotificationBell />
              <button
                onClick={toggleTheme}
                className="p-2.5 rounded-full bg-white/60 dark:bg-white/5 border border-cn-border/60 hover:bg-white transition-colors"
                aria-label="Toggle theme"
                data-testid="theme-toggle"
              >
                {theme === 'light' ? <Moon className="w-4 h-4 text-cn-text-muted" /> : <Sun className="w-4 h-4 text-yellow-400" />}
              </button>
            </div>
          </div>
        </header>

        {children}
      </main>
    </div>
  );
};

export default DashboardShell;
